import { FetchQueryOptions } from '@tanstack/react-query';

import { parkingApi } from '@api/parking.api';
import { LanguageKey } from '@models/Language';
import { ParkingProduct } from '@models/parking.types';
import { RealEstateDocument } from '@models/realEstate.types';
import { realEstateHasParkingSpaceProducts } from '@views/parking/parking.utils';

export const fetchParkingProductsQuery = (
  realEstate: RealEstateDocument | undefined,
  languageKey: LanguageKey
): FetchQueryOptions<ParkingProduct[]> & {
  select: (parkingSpaces: ParkingProduct[]) => {
    rentableSpaces: ParkingProduct[];
    allSpaces: ParkingProduct[];
  };
  enabled: boolean;
} => ({
  queryKey: ['realEstateParkingSpaces', realEstate?.id, languageKey],
  queryFn: async () => {
    if (!realEstate?.id) {
      throw new Error('Real estate ID is required to fetch parking spaces');
    }

    const { status, data } = await parkingApi.getRealEstateParkingProducts(
      realEstate?.id,
      languageKey
    );

    if (status !== 200 || !data) {
      throw Error('Error fetching parking spaces');
    }
    return data.content;
  },
  // Filter out spaces without rent and spaces with specific specialParkingSpace types. Return rentable and all spaces.
  select: (parkingProducts) => ({
    rentableSpaces: parkingProducts.filter(
      (space) =>
        !!space.rent &&
        ![
          'IN_STORAGE_USE',
          'SNOW_DUMP',
          'SHARED_CAR_PARKING_SPACE',
          'QUEST_PARKING_SPACE',
        ].some((type) => type === space.parkingSpace.specialParkingSpace)
    ),
    allSpaces: parkingProducts,
  }),
  enabled: realEstateHasParkingSpaceProducts(realEstate),
});
